/**
 * repositories/invitationTokensRepository.ts
 * Invitation Tokens table data access layer with JOIN support
 *
 * Architecture: WU1.5 Task 3.1 - JOIN Repository Migration
 *
 * Purpose: Centralize all data access for invitation_tokens table,
 * including JOIN queries with beta_applications. Handles mapping between
 * database snake_case and application camelCase at the boundary.
 *
 * ========================================================================
 * JOIN PATTERN
 * ========================================================================
 *
 * PostgREST nested selects behave like LEFT JOINs:
 * - Joined data may be null if FK is invalid
 * - Use assertNotNull() for required joins
 * - Use explicit column projections (no *)
 * - Map base row with toDomain(), joined data with map*Join()
 *
 * ========================================================================
 */

import { supabase } from '@/lib/supabase';
import type { Database } from '@/types/supabase.types';
import { toDomain, toWire, toPatch, throwIfError } from '@/lib/supabaseMappers';
import type {
  InvitationToken,
  InvitationTokenInsert,
  InvitationTokenUpdate
} from '@/types/supabase/InvitationToken';
import type { BetaApplicationStatus } from '@/types/supabase/BetaApplication';

// ============================================================================
// WIRE TYPES (snake_case from database)
// ============================================================================

type InvitationTokenInsertRow = Database['public']['Tables']['invitation_tokens']['Insert'];
type InvitationTokenUpdateRow = Database['public']['Tables']['invitation_tokens']['Update'];

// ============================================================================
// JOIN DOMAIN TYPES (Local to this repository)
// ============================================================================

/**
 * Beta application JOIN domain type (camelCase)
 * Used in repository return types for joined application data
 */
interface BetaAppJoinDomain {
  id: string;
  email: string;
  fullName: string;
  status: BetaApplicationStatus;
}

/**
 * Redacted invitation token (camelCase, no secret token field)
 * Used for RPC responses where the token secret is not returned
 */
export interface InvitationTokenRedacted {
  id: string;
  betaApplicationId: string;
  expiresAt: string;
  usedAt: string | null;
  userId: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Reason why a token is invalid */
export type InvalidReason = 'expired' | 'already_used' | 'not_approved' | 'not_found';

// ============================================================================
// SECURITY PROJECTIONS (GPT Recommendation #1)
// ============================================================================
// TOKEN_SAFE: Default projection WITHOUT token field (prevents accidental leaks)
// TOKEN_VALIDATION: Full projection WITH token field (only for validateToken path)

const TOKEN_SAFE = 'id,beta_application_id,expires_at,used_at,user_id,created_at,updated_at' as const;
const TOKEN_VALIDATION = 'id,beta_application_id,token,expires_at,used_at,user_id,created_at,updated_at' as const;

/**
 * Invitation token with joined application details and validity info
 * Used when fetching token with its associated application info
 */
export interface InvitationWithApplication {
  /** Invitation token record (camelCase, redacted - no secret token) */
  token: InvitationTokenRedacted;
  /** Associated application information (camelCase) */
  application: BetaAppJoinDomain;
  /** Whether the token is valid for activation */
  isValid: boolean;
  /** Reason why token is invalid (null if valid) */
  invalidReason: InvalidReason | null;
}

/**
 * Invitation Tokens Repository
 *
 * Provides data access for invitation_tokens with JOIN support for beta_applications.
 * All methods return camelCase domain types.
 */
export const invitationTokensRepository = {
  /**
   * Get invitation token by token string with application details and validity info
   *
   * Uses SECURITY DEFINER RPC to bypass RLS for anonymous users.
   * Returns token with joined application information and validity status.
   * Unlike previous implementation, this returns tokens even if invalid
   * so callers can distinguish between expired, used, and not_found.
   *
   * @param tokenString - The invitation token string
   * @returns Promise resolving to token with application and validity, or null if not found
   *
   * @example
   * const result = await invitationTokensRepository.getByTokenWithApplication('abc123');
   * if (result) {
   *   if (result.isValid) {
   *     // proceed with activation
   *   } else {
   *     console.log('Invalid:', result.invalidReason);
   *   }
   * }
   */
  async getByTokenWithApplication(tokenString: string): Promise<InvitationWithApplication | null> {
    // Use SECURITY DEFINER RPC to bypass RLS for anonymous users
    const { data, error } = await supabase
      .rpc('validate_invitation_token_with_application', { p_token: tokenString })
      .maybeSingle();

    // Only throw on actual PostgREST errors, not empty results
    if (error) throw error;
    if (!data) return null;

    // Map snake_case RPC response to camelCase domain types
    return {
      token: {
        id: data.id,
        betaApplicationId: data.beta_application_id,
        expiresAt: data.expires_at,
        usedAt: data.used_at,
        userId: data.user_id,
        createdAt: data.created_at,
        updatedAt: data.updated_at
      },
      application: {
        id: data.app_id,
        email: data.email,
        fullName: data.full_name,
        status: data.app_status as BetaApplicationStatus
      },
      isValid: data.is_valid,
      invalidReason: data.invalid_reason as InvalidReason | null
    };
  },

  /**
   * Create new invitation token
   *
   * GPT #1: Uses TOKEN_SAFE projection (no token in return)
   * GPT #2: Enforces one active invite per application
   *
   * @param data - Invitation token data to insert
   * @returns Promise resolving to created token (without token field)
   */
  async create(data: InvitationTokenInsert): Promise<InvitationToken> {
    // GPT #2: Invalidate existing active tokens for this betaApplicationId
    await this.invalidateActiveTokens(data.betaApplicationId);

    const wireData = toWire<InvitationTokenInsertRow>(data);

    const result = await supabase
      .from('invitation_tokens')
      .insert(wireData)
      .select(TOKEN_SAFE)
      .single();

    const created = throwIfError(result);
    return toDomain<InvitationToken>(created);
  },

  /**
   * Update invitation token
   *
   * GPT #4: Uses toPatch() to avoid nulling unrelated columns
   * GPT #1: Uses TOKEN_SAFE projection
   *
   * @param id - Token ID to update
   * @param data - Fields to update
   * @returns Promise resolving to updated token (without token field)
   */
  async update(id: string, data: InvitationTokenUpdate): Promise<InvitationToken> {
    const patchData = toPatch<InvitationTokenUpdateRow>(data);

    const result = await supabase
      .from('invitation_tokens')
      .update(patchData)
      .eq('id', id)
      .select(TOKEN_SAFE)
      .single();

    const updated = throwIfError(result);
    return toDomain<InvitationToken>(updated);
  },

  /**
   * Mark token as used and link to created user
   *
   * Uses SECURITY DEFINER RPC to bypass RLS. Idempotent - safe to call multiple times.
   *
   * @param id - Token ID to mark as used
   * @param userId - User ID that was created
   * @returns Promise resolving to true if updated, false if already used
   */
  async markAsUsed(id: string, userId: string): Promise<boolean> {
    // Use SECURITY DEFINER RPC to bypass RLS
    const { data, error } = await supabase
      .rpc('mark_invitation_token_used', { p_token_id: id, p_user_id: userId });

    if (error) throw error;

    // Returns true if updated, false if already used/not found
    return data === true;
  },

  /**
   * Extend expiration date of invitation token
   *
   * GPT #5: Guards against extending used/expired tokens
   * GPT #4: Uses toPatch()
   *
   * @param id - Token ID to extend
   * @param expiresAt - New expiration date (ISO string)
   * @returns Promise resolving to updated token, or null if already used/expired
   */
  async extendExpiration(id: string, expiresAt: string): Promise<InvitationToken | null> {
    // GPT #5: Guard - check if token is already used or expired
    const existing = await supabase
      .from('invitation_tokens')
      .select('used_at,expires_at')
      .eq('id', id)
      .single();

    const tokenData = throwIfError(existing);
    if (!tokenData) {
      return null;
    }

    // Don't extend if already used
    if (tokenData.used_at) {
      return null;
    }

    // Don't extend if already expired (using server time comparison)
    if (new Date(tokenData.expires_at) < new Date()) {
      return null;
    }

    const updateData: InvitationTokenUpdate = {
      expiresAt
    };

    const patchData = toPatch<InvitationTokenUpdateRow>(updateData);

    const result = await supabase
      .from('invitation_tokens')
      .update(patchData)
      .eq('id', id)
      .select(TOKEN_SAFE)
      .single();

    const updated = throwIfError(result);
    return toDomain<InvitationToken>(updated);
  },

  /**
   * Get active (unused, non-expired) token for beta application
   *
   * GPT #3: Index recommended: (beta_application_id, expires_at) WHERE used_at IS NULL
   * GPT #5: Uses server-side now() for time comparison
   *
   * @param betaApplicationId - Beta application ID
   * @returns Promise resolving to active token or null
   */
  async getActiveToken(betaApplicationId: string): Promise<InvitationToken | null> {
    const result = await supabase
      .from('invitation_tokens')
      .select(TOKEN_SAFE)
      .eq('beta_application_id', betaApplicationId)
      .is('used_at', null)
      .gt('expires_at', new Date().toISOString())  // GPT #5: Server time comparison
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    const data = throwIfError(result);
    if (!data) return null;

    return toDomain<InvitationToken>(data);
  },

  /**
   * Get invitation token by token string
   *
   * GPT #1: Uses TOKEN_VALIDATION projection (includes token field)
   *
   * @param tokenString - Token string to lookup
   * @returns Promise resolving to token or null
   */
  async getByToken(tokenString: string): Promise<InvitationToken | null> {
    const result = await supabase
      .from('invitation_tokens')
      .select(TOKEN_VALIDATION)  // GPT #1: Only path that returns token field
      .eq('token', tokenString)
      .maybeSingle();

    const data = throwIfError(result);
    if (!data) return null;

    return toDomain<InvitationToken>(data);
  },

  /**
   * Clean up expired tokens
   *
   * GPT #9: Testable - only deletes truly expired rows
   * GPT #3: Index recommended: (expires_at)
   *
   * @param olderThanDays - Remove tokens older than this many days
   * @returns Promise resolving to number of tokens deleted
   */
  async cleanExpired(olderThanDays: number): Promise<number> {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - olderThanDays);

    const result = await supabase
      .from('invitation_tokens')
      .delete()
      .lt('expires_at', cutoffDate.toISOString());

    throwIfError(result);
    return result.count || 0;
  },

  /**
   * Invalidate active tokens for beta application (private helper)
   *
   * GPT #2: Enforces one active invite per application
   * Used internally by create() to ensure uniqueness
   *
   * @param betaApplicationId - Beta application ID
   * @returns Promise resolving to number of tokens invalidated
   */
  async invalidateActiveTokens(betaApplicationId: string): Promise<number> {
    // Mark all unused, non-expired tokens as used (invalidate them)
    const result = await supabase
      .from('invitation_tokens')
      .update({ used_at: new Date().toISOString() })
      .eq('beta_application_id', betaApplicationId)
      .is('used_at', null)
      .gt('expires_at', new Date().toISOString());

    throwIfError(result);
    return result.count || 0;
  },

  /**
   * Returns the number of currently-valid invitation tokens (unused + unexpired).
   * Used for admin capacity / pipeline UI.
   *
   * Counts all invitation tokens that:
   * - Have not been used (used_at IS NULL)
   * - Have not expired (expires_at > now)
   *
   * @returns Promise resolving to count of active invitation tokens
   *
   * @example
   * const pendingInvites = await invitationTokensRepository.countActive();
   * console.log(`${pendingInvites} invitations pending`);
   */
  async countActive(): Promise<number> {
    const result = await supabase
      .from('invitation_tokens')
      .select('*', { count: 'exact', head: true })
      .is('used_at', null)
      .gt('expires_at', new Date().toISOString());

    throwIfError(result);
    return result.count || 0;
  }
};
